// ✅ File: src/components/docs_ai_assistant/DocsUploadDrawer.jsx
// -------------------------------------------------------------
// Upload Drawer for AI Document Assistant (Admin / Manager only)
// Handles document uploads (PDF, DOCX, TXT)
// Triggers backend indexing via Celery
// -------------------------------------------------------------

import React, { useState } from "react";
import axiosClient from "../../api/axiosClient";

const DocsUploadDrawer = ({ onClose }) => {
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const [progress, setProgress] = useState(0);

  const handleFileChange = (e) => {
    setSelectedFile(e.target.files[0]);
    setError("");
    setMessage("");
  };

  const handleUpload = async (e) => {
    e.preventDefault();
    if (!selectedFile) {
      setError("Please select a file before uploading.");
      return;
    }

    const formData = new FormData();
    formData.append("file", selectedFile);

    try {
      setUploading(true);
      setError("");
      setMessage("");
      setProgress(0);

      const res = await axiosClient.post("/docs_ai_assistant/upload", formData, {
        headers: { "Content-Type": "multipart/form-data" },
        onUploadProgress: (p) => {
          const percent = Math.round((p.loaded * 100) / p.total);
          setProgress(percent);
        },
      });

      setMessage(res.data?.message || "✅ File uploaded successfully!");
      setSelectedFile(null);
      setUploading(false);
      setProgress(100);
    } catch (err) {
      console.error("Upload failed:", err);
      setError(err?.response?.data?.message || "❌ Failed to upload file.");
      setUploading(false);
      setProgress(0);
    }
  };

  return (
    <>
      {/* Overlay */}
      <div
        className="position-fixed top-0 start-0 w-100 h-100 bg-dark"
        style={{ opacity: 0.4, zIndex: 1050 }}
        onClick={onClose}
      ></div>

      {/* Drawer */}
      <div
        className="ai-drawer open"
        style={{
          position: "fixed",
          top: 0,
          right: 0,
          width: "420px",
          height: "100vh",
          background: "rgba(255, 255, 255, 0.97)",
          backdropFilter: "blur(8px)",
          boxShadow: "-4px 0 12px rgba(0, 0, 0, 0.2)",
          zIndex: 1055,
          overflowY: "auto",
          transition: "all 0.3s ease-in-out",
        }}
      >
        {/* Header */}
        <div
          className="d-flex justify-content-between align-items-center border-bottom p-3 bg-light"
          style={{ position: "sticky", top: 0, zIndex: 1 }}
        >
          <h6 className="mb-0 fw-bold">📤 Upload Document</h6>
          <button className="btn btn-sm btn-outline-danger" onClick={onClose}>
            Close
          </button>
        </div>

        {/* Body */}
        <div className="p-4">
          <p className="text-muted small mb-4">
            Upload your document (PDF, DOCX, or TXT). Once uploaded, it will be indexed
            in the background and available for AI queries.
          </p>

          <form onSubmit={handleUpload}>
            <div className="mb-3">
              <label className="form-label fw-bold">Select File</label>
              <input
                type="file"
                className="form-control"
                accept=".pdf,.docx,.txt"
                onChange={handleFileChange}
              />
            </div>

            {selectedFile && (
              <div className="alert alert-info py-2 small mb-2">
                <i className="fas fa-file"></i> {selectedFile.name}
              </div>
            )}

            {uploading && (
              <div className="progress mb-3">
                <div
                  className="progress-bar progress-bar-striped progress-bar-animated"
                  role="progressbar"
                  style={{ width: `${progress}%` }}
                >
                  {progress}%
                </div>
              </div>
            )}

            {message && (
              <div className="alert alert-success py-2 small mb-2">
                <i className="fas fa-check-circle me-2"></i>
                {message}
              </div>
            )}

            {error && (
              <div className="alert alert-danger py-2 small mb-2">
                <i className="fas fa-exclamation-circle me-2"></i>
                {error}
              </div>
            )}

            <div className="text-end">
              <button
                type="submit"
                className="btn gradient-btn"
                disabled={uploading}
              >
                {uploading ? "Uploading..." : "Upload File"}
              </button>
            </div>
          </form>
        </div>
      </div>
    </>
  );
};

export default DocsUploadDrawer;
